// src/common/notifications/notification-log-helpers.js
import { apiRequest } from '../../../../../services/api-request.js';
import { formatDateTime } from '../../../../../shared/utils/timeformats.js';

// If paths differ between mobile/desktop, just adjust the relative paths above.

// --- CATEGORY CONFIG (icons optional for desktop) ---
import pressureIconUrl from '../../../../../assets/icons/pressure.svg';
import temperatureIconUrl from '../../../../../assets/icons/temperature.svg';
import voltageIconUrl from '../../../../../assets/icons/voltage.svg';
import linkIconUrl from '../../../../../assets/icons/notification-log-link.svg';
import noSignalIconUrl from '../../../../../assets/icons/notification-log-signal.svg';
import pairIconUrl from '../../../../../assets/icons/notification-log-pair.svg';
import rotationIconUrl from '../../../../../assets/icons/notification-log-rotation.svg';

export const CATEGORY_CONFIG = [
  { key: 'pressure', label: 'Pressure', icon: pressureIconUrl },
  { key: 'temperature', label: 'Temperature', icon: temperatureIconUrl },
  { key: 'voltage', label: 'Voltage', icon: voltageIconUrl },
  { key: 'link', label: 'Linked / Unlinked', icon: linkIconUrl },
  { key: 'pair', label: 'Paired / Unpaired', icon: pairIconUrl },
  { key: 'rotation', label: 'Rotation', icon: rotationIconUrl },
  { key: 'signal', label: 'No Signal', icon: noSignalIconUrl },
];

// --- Category resolver (unchanged) ---
export function resolveCategory(subjectRaw = '') {
  const s = String(subjectRaw).toLowerCase();
  if (s.includes('pressure')) return 'pressure';
  if (s.includes('temperature')) return 'temperature';
  if (s.includes('voltage') || s.includes('volt')) return 'voltage';
  if (s.includes('link') || s.includes('unlink')) return 'link';
  if (s.includes('signal') || s.includes('activity')) return 'signal';
  if (s.includes('rotation')) return 'rotation';
  if (s.includes('pair') || s.includes('unpair')) return 'pair';
  return null;
}

export function groupByCategory(items = []) {
  const map = {
    pressure: [],
    temperature: [],
    voltage: [],
    link: [],
    signal: [],
    rotation: [],
    pair: []
  };
  for (const n of items) {
    const key = resolveCategory(n.subject);
    if (key && map[key]) map[key].push(n);
  }
  return map;
}

// --- Banner (card) builder: DOM-agnostic, reusable in desktop ---
export function createNotificationBanner(n, documentObj = document) {
  const chip = documentObj.createElement('div');
  chip.className = 'card';

  const subject = String(n.subject || '').toLowerCase();
  const message = String(n.message || '').toLowerCase();

  let tone = 'neutral';

  if (
    subject.includes('unlink') ||
    subject.includes('unlinked') ||
    message.includes('unlink')
  ) {
    tone = 'unlink';
  } else if (
    subject.includes('link') ||
    subject.includes('linked') ||
    message.includes('linked')
  ) {
    tone = 'link';
  } else if (subject.includes('activity') || message.includes('activity')) {
    tone = 'nosignal';
  } else if (subject.includes('rotation') || message.includes('rotation')) {
    tone = 'rotation';
  } else {
    const isHighPressure =
      /(^|\b)(high|over|above|max|too high)(\b|$)/.test(subject) ||
      /(^|\b)(high|over|above|max|too high)(\b|$)/.test(message);

    const isLowPressure =
      /(^|\b)(low|under|below|min|too low)(\b|$)/.test(subject) ||
      /(^|\b)(low|under|below|min|too low)(\b|$)/.test(message);

    const isHighTemperature =
      /(^|\b)(temperature)(\b|$)/.test(subject) ||
      /(^|\b)(temperature)(\b|$)/.test(message);

    const isLowVoltage =
      /(^|\b)(voltage|volt)(\b|$)/.test(subject) ||
      /(^|\b)(voltage|volt)(\b|$)/.test(message);

    if (isHighPressure) tone = 'high-pressure';
    if (isLowPressure) tone = 'low-pressure';
    if (isHighTemperature) tone = 'high-temperature';
    if (isLowVoltage) tone = 'low-voltage';
  }

  chip.classList.add(`tone-${tone}`);

  const title = documentObj.createElement('div');
  title.className = 'notif-chip-title';
  title.textContent = n.subject || 'Notification';

  const sub = documentObj.createElement('div');
  sub.className = 'notif-chip-sub';
  const ts = new Date(n.ts || n.timestamp || Date.now());
  sub.textContent = `${formatDateTime(ts)}`;

  const body = documentObj.createElement('div');
  body.className = 'notif-chip-body';
  body.textContent = n.message || '';

  chip.appendChild(title);
  chip.appendChild(sub);
  chip.appendChild(body);
  return chip;
}

// --- API fetcher (same endpoint logic as mobile) ---
export async function getNotifications(unit) {
  const endpoint =
    unit.unitType === 'TRAILER'
      ? `api/unit/standalone/Trailer/${unit.unitId}/notification-logs`
      : `api/unit/${unit.imei}/notification-logs`;

  const notificationLogs = await apiRequest(endpoint);
  return Array.isArray(notificationLogs?.notifications)
    ? notificationLogs.notifications
    : [];
}
